/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { Close, WarningAmberOutlined } from '@mui/icons-material'
import { useApolloClient, useLazyQuery, useQuery } from '@apollo/client'
import {
  CLASSIFICATION_PREVIEW_CONTEXT,
  SABRE_GET_PRODUCT,
  type UltraDAIZeroReviewCropFieldsFragment,
} from '@dai/graphql'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import React from 'react'

import useT from '@/i18n/useT'

import PanZoomImageViewer, {
  PanZoomImageViewerHandle,
  PanZoomImageViewerRenderContext,
} from './PanZoomImageViewer'
import ProductImageBox from './ProductImageBox'
import {
  ClassificationPreviewContextData,
  ClassificationPreviewContextVariables,
  getMatchingProductByUpc,
  getProductDisplayName,
  ProductByUpcData,
  ProductDetails,
  ProductByUpcVariables,
} from './labelingHelpers'
import { getRenderedBoxRect } from './classificationUtils'
import type { ClassificationBoundingBox } from './classificationTypes'

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value))

type FullImageDialogProps = {
  box: ClassificationBoundingBox | UltraDAIZeroReviewCropFieldsFragment | null
  open: boolean
  onClose: () => void
  selectedUpc?: string | null
  embedded?: boolean
  showSelectedProduct?: boolean
  fetchPreviewContext?: boolean
}

const FullImageDialog: React.FC<FullImageDialogProps> = ({
  box,
  open,
  onClose,
  selectedUpc = null,
  embedded = false,
  showSelectedProduct = true,
  fetchPreviewContext = true,
}) => {
  const { t } = useT()
  const apolloClient = useApolloClient()
  const viewerRef = React.useRef<PanZoomImageViewerHandle | null>(null)
  const hoverClearTimeoutRef = React.useRef<number | null>(null)
  const [previewContext, setPreviewContext] =
    React.useState<
      ClassificationPreviewContextData['classificationPreviewContext']
    >(null)
  const [previewLoading, setPreviewLoading] = React.useState(false)
  const [hoveredNearbyBoxId, setHoveredNearbyBoxId] = React.useState<
    string | null
  >(null)
  const [nearbyProductsByUpc, setNearbyProductsByUpc] = React.useState<
    Record<string, ProductDetails | null>
  >({})
  const [naturalImageSize, setNaturalImageSize] = React.useState<{
    width: number
    height: number
  } | null>(null)
  const [imageLoadFailed, setImageLoadFailed] = React.useState(false)
  const [embeddedFocusReady, setEmbeddedFocusReady] = React.useState(!embedded)
  const [loadPreviewContext] = useLazyQuery<
    ClassificationPreviewContextData,
    ClassificationPreviewContextVariables
  >(CLASSIFICATION_PREVIEW_CONTEXT, {
    context: { endPoint: 'mlUltra' },
    fetchPolicy: 'network-only',
  })

  const displayedBox = React.useMemo(() => {
    if (!box || !('cropId' in box)) return box

    return {
      id: box.cropId,
      sourceImageUrl: box.imageUrl,
      minX: box.minX,
      maxX: box.maxX,
      minY: box.minY,
      maxY: box.maxY,
    }
  }, [box])
  const previewImageUrl =
    previewContext?.sourceImageUrl ?? displayedBox?.sourceImageUrl ?? ''
  const hoveredNearbyBox =
    previewContext?.nearbyBoundingBoxes.find(
      nearbyBox => nearbyBox.id === hoveredNearbyBoxId,
    ) ?? null
  const hoveredUpc =
    hoveredNearbyBox?.verifiedUpc ?? hoveredNearbyBox?.currentUpc ?? ''
  const { data: selectedProductData } = useQuery<
    ProductByUpcData,
    ProductByUpcVariables
  >(SABRE_GET_PRODUCT, {
    context: { endPoint: 'sabre' },
    variables: { upc: selectedUpc ?? '' },
    skip: !selectedUpc,
    fetchPolicy: 'cache-first',
  })
  const hoveredProduct = getMatchingProductByUpc(
    hoveredUpc ? nearbyProductsByUpc[hoveredUpc] ?? null : null,
    hoveredUpc,
  )
  const selectedProduct = getMatchingProductByUpc(
    selectedProductData?.product ?? null,
    selectedUpc,
  )
  const imageLoadFailedMessage = t(
    'fullImageDialog.imageLoadFailed',
    'Image failed to load. Try refreshing the page. If it still fails to load, flag the crop.',
  )

  React.useEffect(() => {
    return () => {
      if (hoverClearTimeoutRef.current !== null) {
        window.clearTimeout(hoverClearTimeoutRef.current)
      }
    }
  }, [])

  const clearHoveredNearbyBox = React.useCallback((boxId?: string) => {
    if (hoverClearTimeoutRef.current !== null) {
      window.clearTimeout(hoverClearTimeoutRef.current)
    }

    hoverClearTimeoutRef.current = window.setTimeout(() => {
      setHoveredNearbyBoxId(current => {
        if (boxId && current !== boxId) {
          return current
        }

        return null
      })
      hoverClearTimeoutRef.current = null
    }, 120)
  }, [])

  const setHoveredNearbyBox = React.useCallback((boxId: string) => {
    if (hoverClearTimeoutRef.current !== null) {
      window.clearTimeout(hoverClearTimeoutRef.current)
      hoverClearTimeoutRef.current = null
    }

    setHoveredNearbyBoxId(boxId)
  }, [])

  React.useEffect(() => {
    let active = true

    if (!displayedBox || !open || !fetchPreviewContext) {
      setPreviewContext(null)
      setPreviewLoading(false)
      setHoveredNearbyBoxId(null)
      setNearbyProductsByUpc({})
      return () => {
        active = false
      }
    }

    setPreviewContext(null)
    setPreviewLoading(true)
    setHoveredNearbyBoxId(null)
    setNearbyProductsByUpc({})

    void loadPreviewContext({
      variables: { boxId: displayedBox.id },
    })
      .then(result => {
        if (!active) return
        setPreviewContext(result.data?.classificationPreviewContext ?? null)
      })
      .catch(() => {
        if (!active) return
        setPreviewContext(null)
      })
      .finally(() => {
        if (active) {
          setPreviewLoading(false)
        }
      })

    return () => {
      active = false
    }
  }, [displayedBox, fetchPreviewContext, loadPreviewContext, open])

  React.useEffect(() => {
    if (!open || !previewContext) {
      return
    }

    const upcs = Array.from(
      new Set(
        previewContext.nearbyBoundingBoxes
          .flatMap(nearbyBox => [nearbyBox.verifiedUpc, nearbyBox.currentUpc])
          .filter((upc): upc is string => Boolean(upc)),
      ),
    )

    if (upcs.length === 0) {
      return
    }

    let active = true

    void Promise.all(
      upcs.map(async upc => {
        try {
          const result = await apolloClient.query<
            ProductByUpcData,
            ProductByUpcVariables
          >({
            query: SABRE_GET_PRODUCT,
            context: { endPoint: 'sabre' },
            variables: { upc },
            fetchPolicy: 'cache-first',
          })

          return [upc, result.data.product ?? null] as const
        } catch {
          return [upc, null] as const
        }
      }),
    ).then(results => {
      if (!active) return

      setNearbyProductsByUpc(current => {
        const next = { ...current }

        results.forEach(([upc, product]) => {
          next[upc] = product
        })

        return next
      })
    })

    return () => {
      active = false
    }
  }, [apolloClient, open, previewContext])

  React.useEffect(() => {
    if (!previewImageUrl) {
      setNaturalImageSize(null)
      setImageLoadFailed(true)
      return
    }
    let active = true
    const timeoutId = window.setTimeout(() => {
      if (!active) return
      setImageLoadFailed(true)
    }, 15000)

    setNaturalImageSize(null)
    setImageLoadFailed(false)
    const img = new window.Image()
    img.onload = () => {
      if (!active) return
      window.clearTimeout(timeoutId)
      setImageLoadFailed(false)
      setNaturalImageSize({
        width: img.naturalWidth,
        height: img.naturalHeight,
      })
    }
    img.onerror = () => {
      if (!active) return
      window.clearTimeout(timeoutId)
      setImageLoadFailed(true)
    }
    img.src = previewImageUrl

    return () => {
      active = false
      window.clearTimeout(timeoutId)
    }
  }, [previewImageUrl])

  React.useEffect(() => {
    if (!embedded) {
      setEmbeddedFocusReady(true)
      return
    }

    setEmbeddedFocusReady(false)
  }, [displayedBox?.id, embedded, naturalImageSize, open, previewImageUrl])

  React.useEffect(() => {
    if (
      !embedded ||
      !open ||
      !displayedBox ||
      previewLoading ||
      !naturalImageSize
    ) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      viewerRef.current?.focusBox(displayedBox)
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          setEmbeddedFocusReady(true)
        })
      })
    }, 0)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [displayedBox, embedded, naturalImageSize, open, previewLoading])

  const showEmbeddedLoading =
    embedded &&
    !imageLoadFailed &&
    (!embeddedFocusReady || previewLoading || !naturalImageSize)

  const overlayBoxes = React.useMemo(() => {
    if (!displayedBox) return []

    return [
      ...(previewContext?.nearbyBoundingBoxes.map(nearbyBox => ({
        ...nearbyBox,
        color: '#94a3b8',
        fillColor: 'transparent',
        borderRadius: 2,
        borderWidth: 2,
        cursor: 'help',
        onMouseEnter: () => setHoveredNearbyBox(nearbyBox.id),
        onMouseLeave: () => clearHoveredNearbyBox(nearbyBox.id),
      })) ?? []),
      {
        ...displayedBox,
        color: '#ff6b2c',
        variant: 'spotlight' as const,
        spotlightOverlayColor: 'transparent',
        borderRadius: 0,
        borderWidth: 4,
      },
    ]
  }, [
    clearHoveredNearbyBox,
    displayedBox,
    previewContext?.nearbyBoundingBoxes,
    setHoveredNearbyBox,
  ])

  const renderViewportOverlay = React.useCallback(
    (context: PanZoomImageViewerRenderContext) => {
      if (!hoveredNearbyBox || !context.naturalSize) {
        return null
      }

      const rect = getRenderedBoxRect(
        hoveredNearbyBox,
        context.naturalSize,
        context.renderedSize,
      )
      const tooltipMaxWidth = 300
      const tooltipEstimatedHeight = 72
      const viewportPadding = 8
      const offsetLeft =
        context.containerSize.width / 2 -
        context.renderedSize.width / 2 +
        context.pan.x
      const offsetTop =
        context.containerSize.height / 2 -
        context.renderedSize.height / 2 +
        context.pan.y
      const productLabel = getProductDisplayName(hoveredProduct) ?? null
      const title = hoveredNearbyBox.verifiedUpc
        ? t('fullImageDialog.hover.verified', 'Verified product')
        : t('fullImageDialog.hover.predicted', 'Predicted product')
      const predictionWarning = t(
        'fullImageDialog.hover.predictionWarning',
        'This box has not been verified yet, and should not be trusted 100%',
      )
      const preferredLeft =
        offsetLeft + rect.left + rect.width / 2 - tooltipMaxWidth / 2
      const left = clamp(
        preferredLeft,
        viewportPadding,
        Math.max(
          viewportPadding,
          context.containerSize.width - tooltipMaxWidth - viewportPadding,
        ),
      )
      const preferredTop = offsetTop + rect.top - 44
      const top = clamp(
        preferredTop < viewportPadding
          ? offsetTop + rect.top + rect.height + 8
          : preferredTop,
        viewportPadding,
        Math.max(
          viewportPadding,
          context.containerSize.height -
            tooltipEstimatedHeight -
            viewportPadding,
        ),
      )

      if (!hoveredUpc) {
        return null
      }

      return (
        <Box
          sx={{
            position: 'absolute',
            left,
            top,
            px: 1,
            py: 0.75,
            borderRadius: 1,
            bgcolor: 'rgba(15, 23, 42, 0.9)',
            color: '#fff',
            pointerEvents: 'auto',
            maxWidth: tooltipMaxWidth,
            boxShadow: 3,
            display: 'flex',
            gap: 1,
            alignItems: 'flex-start',
          }}
          onMouseEnter={() => setHoveredNearbyBox(hoveredNearbyBox.id)}
          onMouseLeave={() => clearHoveredNearbyBox(hoveredNearbyBox.id)}
        >
          <ProductImageBox
            imageUrl={hoveredProduct?.marketingImageUrl}
            size={44}
            noImageLabel={t(
              'classification.anchorImage.unavailable',
              'No image',
            )}
          />
          <Box>
            <Typography
              variant="caption"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                opacity: 0.8,
              }}
            >
              {title}
              {!hoveredNearbyBox.verifiedUpc ? (
                <Tooltip title={predictionWarning} placement="top" arrow>
                  <Box
                    component="span"
                    aria-label={predictionWarning}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      color: '#facc15',
                    }}
                  >
                    <WarningAmberOutlined sx={{ fontSize: 16 }} />
                  </Box>
                </Tooltip>
              ) : null}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>
              {`UPC ${hoveredUpc}`}
            </Typography>
            {productLabel ? (
              <Typography variant="caption" sx={{ display: 'block' }}>
                {productLabel}
              </Typography>
            ) : null}
          </Box>
        </Box>
      )
    },
    [
      clearHoveredNearbyBox,
      hoveredNearbyBox,
      hoveredProduct,
      hoveredUpc,
      setHoveredNearbyBox,
      t,
    ],
  )

  const viewerHeight = embedded ? 'calc(100dvh - 260px)' : '100%'

  const content = (
    <Stack
      spacing={1.5}
      sx={{ height: embedded ? 'auto' : '100%', minHeight: 0 }}
    >
      {selectedUpc && showSelectedProduct ? (
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={theme => ({
            p: 1.25,
            borderRadius: 2,
            border: '1px solid',
            borderColor: theme.palette.divider,
            backgroundColor: theme.palette.background.default,
          })}
        >
          <ProductImageBox
            imageUrl={selectedProduct?.marketingImageUrl}
            size={56}
            noImageLabel={t(
              'classification.anchorImage.unavailable',
              'No image',
            )}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
              {t('fullImageDialog.selectedProduct.label', 'Selected crop')}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              {getProductDisplayName(selectedProduct) ??
                `${t('fullImageDialog.selectedProduct.upc', 'UPC')} ${selectedUpc}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {`UPC ${selectedUpc}`}
            </Typography>
          </Box>
        </Stack>
      ) : null}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
      >
        <Stack spacing={0.5}>
          <Typography variant="body2" color="text.secondary">
            {t(
              'fullImageDialog.instructions',
              'Click and drag to pan. Zoom with the mouse wheel',
            )}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {previewLoading ? <CircularProgress size={12} /> : null}
            <Typography variant="caption" color="text.secondary">
              {t(
                'fullImageDialog.contextHint',
                'Selected crop is highlighted in orange. Nearby boxes are shown for context when available.',
              )}
            </Typography>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            onClick={() =>
              displayedBox && viewerRef.current?.focusBox(displayedBox)
            }
          >
            {t('fullImageDialog.actions.focusProduct', 'Focus product')}
          </Button>
          <Button onClick={() => viewerRef.current?.reset()}>
            {t('fullImageDialog.actions.resetView', 'Reset View')}
          </Button>
        </Stack>
      </Stack>
      {displayedBox && (
        <Box
          sx={{
            width: '100%',
            flex: embedded ? 'initial' : 1,
            minHeight: embedded ? 420 : 0,
            display: 'flex',
          }}
        >
          <Box
            sx={
              naturalImageSize
                ? {
                    width: '100%',
                    height: viewerHeight,
                    minHeight: embedded ? 420 : 0,
                    position: 'relative',
                  }
                : {
                    width: '100%',
                    height: viewerHeight,
                    minHeight: embedded ? 420 : 0,
                    position: 'relative',
                  }
            }
          >
            {showEmbeddedLoading ? (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                }}
              >
                <CircularProgress size={28} />
              </Box>
            ) : null}
            {imageLoadFailed ? (
              <Box
                sx={theme => ({
                  height: '100%',
                  minHeight: 280,
                  display: 'grid',
                  placeItems: 'center',
                  textAlign: 'center',
                  color: theme.palette.text.secondary,
                  bgcolor: theme.palette.background.default,
                  borderRadius: 1,
                  p: 3,
                })}
              >
                <Stack spacing={1} alignItems="center">
                  <WarningAmberOutlined color="warning" />
                  <Typography variant="body2">
                    {imageLoadFailedMessage}
                  </Typography>
                </Stack>
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  opacity: showEmbeddedLoading ? 0 : 1,
                  transition: showEmbeddedLoading
                    ? 'none'
                    : 'opacity 120ms ease-out',
                }}
              >
                <PanZoomImageViewer
                  ref={viewerRef}
                  imageUrl={previewImageUrl}
                  imageLoadFailedMessage={imageLoadFailedMessage}
                  boxes={overlayBoxes}
                  autoFocusBox={open ? displayedBox : null}
                  height="100%"
                  fitGutterPx={naturalImageSize ? 0 : 48}
                  minZoom={0.8}
                  maxZoom={8}
                  renderViewportOverlay={renderViewportOverlay}
                  resetKey={displayedBox.id}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Stack>
  )

  if (embedded) {
    return content
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 'min(82vw, 1180px)',
          height: '92vh',
          maxWidth: 'none',
        },
      }}
    >
      <DialogTitle sx={{ pr: 7, py: 1 }}>
        {t('fullImageDialog.title', 'Inspect Source Image')}
        <IconButton
          aria-label={t('fullImageDialog.close.ariaLabel', 'Close preview')}
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 1, minHeight: 0 }}>{content}</DialogContent>
    </Dialog>
  )
}

export default FullImageDialog
